import type { RaiseResult, ReconciledDriver, ReconciledIncentive } from "@/hooks/useIncentives"

export const pounds = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })

export const HELD = "Waiting on Moneta review"

export const routesText = (count: number): string => `${count} ${count === 1 ? "route" : "routes"}`

// Nothing raises a driver with a raised row again, whatever Ops do with its ticket. A failed row is retried.
export const isRaised = (driver: ReconciledDriver): boolean => driver.ticket?.status === "raised"

export const isHeld = (driver: ReconciledDriver): boolean =>
  driver.status === "waiting" && !driver.paid && !isRaised(driver)

export const isReady = (driver: ReconciledDriver): boolean =>
  driver.status === "ready" && !driver.paid && !isRaised(driver)

export const upTo = (amount: number): string => `Up to ${pounds.format(amount)}`

const roundToPence = (amount: number): number => Math.round(amount * 100) / 100

const sumOf = (amounts: number[]): number =>
  roundToPence(amounts.reduce((sum, amount) => sum + amount, 0))

// A held driver's amount is the most Moneta review could leave them owed, and the daily run raises them once ready.
// Raised is what the tickets for this period were raised for, which a correction since can leave out of date.
export const splitOwed = (
  incentives: ReconciledIncentive[],
): { ready: number; held: number; raised: number } => {
  const drivers = incentives.flatMap((incentive) => incentive.drivers)
  return {
    ready: sumOf(drivers.filter(isReady).map((driver) => driver.amount_owed)),
    held: sumOf(drivers.filter(isHeld).map((driver) => driver.amount_owed)),
    raised: sumOf(
      drivers
        .filter((driver) => !driver.paid && isRaised(driver))
        .map((driver) => driver.ticket.amount),
    ),
  }
}

// Complete when nothing failed or was left for another run. Held drivers are expected, so don't count.
export const raiseSummary = ({
  raised,
  failed,
  held,
  deferred,
}: RaiseResult): { text: string; complete: boolean } => {
  const parts = [
    `${raised.length} raised`,
    failed.length > 0 && `${failed.length} failed`,
    held.length > 0 && `${held.length} still ${HELD.toLowerCase()}`,
    deferred.length > 0 && `${deferred.length} not reached in time, so run it again`,
  ].filter(Boolean)
  return {
    text: `Tickets: ${parts.join(", ")}`,
    complete: failed.length === 0 && deferred.length === 0,
  }
}
